if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.prevButton = this.querySelector('.product-media-modal__nav--prev');
        this.nextButton = this.querySelector('.product-media-modal__nav--next');
        this.container = this.querySelector('[role="document"]');
        
        if (this.prevButton) {
          this.prevButton.addEventListener('click', () => this.navigateSlide(-1));
        }
        if (this.nextButton) {
          this.nextButton.addEventListener('click', () => this.navigateSlide(1));
        }
        if (this.container) {
          this.container.addEventListener('scroll', () => {
            this.updateNavButtons();
          });
        }
      }

      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();
        
        // Initialize zoom handlers for all images inside the modal on touch devices
        setTimeout(() => {
          this.initZoomForMobile();
        }, 100);
      }

      hide() {
        super.hide();
        this.resetAllZoom();
      }

      show(opener) {
        super.show(opener);
        this.resetAllZoom();
        this.showActiveMedia();
      }

      navigateSlide(direction) {
        if (!this.container) return;
        const width = this.container.clientWidth;
        const currentScroll = this.container.scrollLeft;
        const targetScroll = currentScroll + direction * width;
        this.container.scrollTo({
          left: targetScroll,
          behavior: 'smooth'
        });
      }

      updateNavButtons() {
        if (!this.container || !this.prevButton || !this.nextButton) return;
        const scrollLeft = this.container.scrollLeft;
        const maxScroll = this.container.scrollWidth - this.container.clientWidth;
        
        // 5px threshold for scroll snapping inaccuracies
        if (scrollLeft <= 5) {
          this.prevButton.setAttribute('disabled', 'disabled');
        } else {
          this.prevButton.removeAttribute('disabled');
        }
        
        if (scrollLeft >= maxScroll - 5) {
          this.nextButton.setAttribute('disabled', 'disabled');
        } else {
          this.nextButton.removeAttribute('disabled');
        }
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');

        const container = this.querySelector('[role="document"]');
        if (window.innerWidth < 750 && container) {
          const originalScrollBehavior = container.style.scrollBehavior;
          container.style.scrollBehavior = 'auto';
          
          setTimeout(() => {
            container.scrollLeft = activeMedia.offsetLeft;
            container.style.scrollBehavior = originalScrollBehavior;
            this.updateNavButtons();
          }, 50);
        } else {
          activeMedia.scrollIntoView();
          if (container) {
            container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
          }
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      initZoomForMobile() {
        if (!this.container) return;
        const images = this.querySelectorAll('.product-media-modal__content img');
        images.forEach((img) => {
          this.setupZoomForImage(img);
        });
      }

      setupZoomForImage(img) {
        const container = this.container;
        let scale = 1;
        let startScale = 1;
        let startX = 0;
        let startY = 0;
        let translateX = 0;
        let translateY = 0;
        let startTranslateX = 0;
        let startTranslateY = 0;
        let lastTouchTime = 0;
        let initialTouchDistance = 0;
        let initialMidX = 0;
        let initialMidY = 0;
        let isPinching = false;
        let isPanning = false;

        function updateTransform(animate = false) {
          if (animate) {
            img.style.transition = 'transform 0.2s ease-out';
          } else {
            img.style.transition = 'none';
          }
          img.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
          
          if (scale > 1) {
            container.style.overflowX = 'hidden';
            container.style.scrollSnapType = 'none';
          } else {
            container.style.overflowX = 'auto';
            container.style.scrollSnapType = 'x mandatory';
          }
        }

        function resetZoom() {
          scale = 1;
          translateX = 0;
          translateY = 0;
          updateTransform(true);
        }

        img.resetZoom = resetZoom;

        img.addEventListener('touchstart', (e) => {
          const now = Date.now();
          if (now - lastTouchTime < 300) {
            e.preventDefault();
            if (scale > 1) {
              resetZoom();
            } else {
              const touch = e.touches[0];
              const rect = img.getBoundingClientRect();
              const tapX = touch.clientX - (rect.left + rect.width / 2);
              const tapY = touch.clientY - (rect.top + rect.height / 2);
              
              scale = 2.5;
              translateX = -tapX * (scale - 1);
              translateY = -tapY * (scale - 1);
              
              clampTranslate();
              updateTransform(true);
            }
            lastTouchTime = 0;
            return;
          }
          lastTouchTime = now;

          if (e.touches.length === 1) {
            isPanning = scale > 1;
            if (isPanning) {
              const touch = e.touches[0];
              startX = touch.clientX;
              startY = touch.clientY;
              startTranslateX = translateX;
              startTranslateY = translateY;
            }
          } else if (e.touches.length === 2) {
            isPinching = true;
            const t1 = e.touches[0];
            const t2 = e.touches[1];
            initialTouchDistance = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
            initialMidX = (t1.clientX + t2.clientX) / 2;
            initialMidY = (t1.clientY + t2.clientY) / 2;
            startScale = scale;
            startTranslateX = translateX;
            startTranslateY = translateY;
          }
        }, { passive: false });

        img.addEventListener('touchmove', (e) => {
          if (isPinching && e.touches.length === 2) {
            e.preventDefault();
            const t1 = e.touches[0];
            const t2 = e.touches[1];
            const dist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
            
            const newScale = startScale * (dist / initialTouchDistance);
            scale = Math.min(Math.max(newScale, 1), 4);
            
            const midX = (t1.clientX + t2.clientX) / 2;
            const midY = (t1.clientY + t2.clientY) / 2;
            const dx = midX - initialMidX;
            const dy = midY - initialMidY;
            
            translateX = startTranslateX + dx;
            translateY = startTranslateY + dy;
            
            clampTranslate();
            updateTransform(false);
          } else if (isPanning && e.touches.length === 1 && scale > 1) {
            e.preventDefault();
            e.stopPropagation();
            
            const touch = e.touches[0];
            const dx = touch.clientX - startX;
            const dy = touch.clientY - startY;
            
            translateX = startTranslateX + dx;
            translateY = startTranslateY + dy;
            
            clampTranslate();
            updateTransform(false);
          }
        }, { passive: false });

        img.addEventListener('touchend', (e) => {
          if (isPinching) {
            isPinching = false;
            if (scale < 1.05) {
              resetZoom();
            } else {
              clampTranslate();
              updateTransform(true);
            }
          } else if (isPanning) {
            isPanning = false;
            clampTranslate();
            updateTransform(true);
          }
        });

        function clampTranslate() {
          const zoomedWidth = img.offsetWidth * scale;
          const zoomedHeight = img.offsetHeight * scale;
          const parentWidth = container.clientWidth;
          const parentHeight = container.clientHeight;
          
          const maxTx = Math.max(0, (zoomedWidth - parentWidth) / 2);
          const maxTy = Math.max(0, (zoomedHeight - parentHeight) / 2);
          
          translateX = Math.min(Math.max(translateX, -maxTx), maxTx);
          translateY = Math.min(Math.max(translateY, -maxTy), maxTy);
        }
      }

      resetAllZoom() {
        const images = this.querySelectorAll('.product-media-modal__content img');
        images.forEach((img) => {
          if (img.resetZoom) img.resetZoom();
        });
      }
    }
  );
}
